/**
 * How a turn's header and step list read, from its model (`turnModel.ts`): the words of each
 * row in its state, the header's lead and icon, when the clock and the "Still …" line show, the
 * window over a long list, and when the list is open. Pure, so the web's components and the
 * native engine's report (`engineTurn.ts`) read one set of rules.
 */
import { helperPhrase } from "./planModel";
import {
    clockText,
    continuing,
    isCountedStep,
    pastTense,
    resultText,
    statusText,
    turnSummary,
    type MarvinOp,
    type TurnModel,
    type TurnPhase,
    type TurnStep,
} from "./turnModel";

/** The header shows the turn's time once it has run this long. */
export const CLOCK_FROM_SECONDS = 5;
/** A step running this long gets a line under it saying it is still going. */
export const STILL_FROM_SECONDS = 20;
/** The synthetic row of a turn waiting for the user's answer. */
export const WAITING_ROW_ID = "waiting";
/** A list longer than this shows only its newest {@link WINDOW_ROWS} rows until asked. */
export const WINDOW_FROM_ROWS = 6;
export const WINDOW_ROWS = 5;
/** What the header and the waiting row say while a question card waits. */
export const WAITING_TEXT = "Needs your answer";

/** The running step in its own words: a helper with a job says it ("MiniMarv is reading ..."). */
export function currentWords(step: TurnStep): string {
    const phrase = step.helper ? helperPhrase(step.task, true) : null;
    if (phrase) return `${step.helper} is ${phrase}`;
    if (step.helper && step.task) return `${step.helper} · ${step.task}`;
    return step.helper ? `${step.helper} · ${step.label}` : step.label;
}

/** What a row says in its state; a helper's row opens with the helper's name. */
export function rowText(step: TurnStep): string {
    // A helper whose job has no verb the client can put in words still shows the job, not its lookups.
    if (step.helper && step.task && step.status === "done") return `${step.helper} · ${step.task}`;
    const text = stateText(step);
    return step.helper ? `${step.helper} · ${text}` : text;
}

export function stateText(step: TurnStep): string {
    switch (step.status) {
        case "done":
            return pastTense(step.label);
        case "failed":
            return `Couldn't finish ${continuing(step.label)}`;
        case "stopped":
            return `Stopped while ${continuing(step.label)}`;
        case "unfinished":
            return `Didn't finish ${continuing(step.label)}`;
        default:
            return step.label;
    }
}

/** A MiniMarv's job in words for its row (board S1b): "reading the change notes …" while it runs. */
export function rowHelperPhrase(step: TurnStep): string | null {
    return step.helper ? helperPhrase(step.task, step.status === "running") : null;
}

/** " · 5 sheets" after a finished row: what it found. */
export function rowResult(step: TurnStep): string | null {
    return step.status === "done" ? resultText(step.resultCount, step.resultNoun) : null;
}

/** Where a line's time goes, for a client that counts it itself (native fills it with `clockText`). */
export const CLOCK_TOKEN = "{clock}";
/** The header's clock, after the step count: " · 0:12". */
export const HEADER_CLOCK_FORMAT = ` · ${CLOCK_TOKEN}`;

/** A line's words with its time filled in at its {@link CLOCK_TOKEN} (the last one: a label never moves it). */
export function fillClock(format: string, seconds: number): string {
    const at = format.lastIndexOf(CLOCK_TOKEN);
    return at < 0
        ? format
        : `${format.slice(0, at)}${clockText(seconds)}${format.slice(at + CLOCK_TOKEN.length)}`;
}

/** The running step's second line, its time on the step at {@link CLOCK_TOKEN}. */
export function stillFormat(label: string): string {
    return `Still ${continuing(label)} · ${CLOCK_TOKEN} on this step`;
}

/** "Still searching … · 0:22 on this step", for the running step past {@link STILL_FROM_SECONDS}. */
export function stillText(label: string, seconds: number): string {
    return fillClock(stillFormat(label), seconds);
}

/** The list's rows: the steps, and while a question card waits, the row that says so. */
export function turnRows(turn: TurnModel): TurnStep[] {
    return turn.waiting
        ? [
              ...turn.steps,
              {
                  id: WAITING_ROW_ID,
                  op: "check",
                  label: WAITING_TEXT,
                  status: "running",
              },
          ]
        : turn.steps;
}

/** The row in flight: the waiting row while a question waits, else the running step. */
export function currentRowId(turn: TurnModel): string | null {
    return turn.waiting ? WAITING_ROW_ID : (turn.current?.id ?? null);
}

/** The steps the header counts (narration and the plan are not steps). */
export function countedSteps(steps: TurnStep[]): number {
    return steps.filter(isCountedStep).length;
}

/**
 * Whether the step list is open before the user toggles it: while the turn is live and no answer
 * text has arrived. It folds on its own when the answer starts; a finished turn stays folded.
 */
export function openByDefault(phase: TurnPhase, hasText: boolean): boolean {
    return phase === "live" && !hasText;
}

/** Whether a turn that is not Deep research draws its header: a finished one that counted no step does not. */
export function headerShown(phase: TurnPhase, turn: TurnModel): boolean {
    const live = phase === "live";
    const ended = phase === "cancelled" || phase === "error";
    return live || ended || countedSteps(turn.steps) > 0;
}

/**
 * The live header's mark (canvas page M, M-04): working while Marvin works, amber while he waits
 * on the user, still once the answer is being written. Null outside a live turn, whose head (if
 * any) is its op icon: stopped or failed.
 */
export function headMark(
    phase: TurnPhase,
    turn: TurnModel,
): "working" | "needsYou" | "rest" | null {
    if (phase !== "live") return null;
    return turn.waiting ? "needsYou" : turn.writing ? "rest" : "working";
}

/**
 * What Marvin is doing now, as an operation; none once the turn finished. The engine reports it
 * to native, whose live header maps it to a mark state as `headMark` does here; the web draws it
 * as the head icon only for a stopped or failed turn.
 */
export function headOp(phase: TurnPhase, turn: TurnModel): MarvinOp | null {
    const live = phase === "live";
    return phase === "cancelled"
        ? "stopped"
        : phase === "error"
          ? "error"
          : !live
            ? null
            : turn.waiting
              ? "wait"
              : turn.writing
                ? "write"
                : (turn.current?.op ?? "think");
}

/**
 * The header's words. While Marvin works they say what he is doing now, in the running step's
 * words ("Searching contracts…", "Thinking…" between steps); a finished turn says what its steps
 * covered, and a stopped or dropped one says so. `title` is the running step's whole label.
 */
export function turnLead(
    phase: TurnPhase,
    turn: TurnModel,
): { lead: string | null; title: string | null } {
    const live = phase === "live";
    const { current, waiting, writing } = turn;
    let lead: string | null;
    if (phase === "cancelled") lead = "Stopped";
    else if (phase === "error") lead = "Couldn't finish";
    else if (!live) lead = turnSummary(turn.steps);
    else if (waiting) lead = WAITING_TEXT;
    else if (writing) lead = "Writing the answer…";
    else lead = current ? statusText(currentWords(current)) : "Thinking…";
    const title = live && !waiting && !writing && current ? current.label : lead;
    return { lead, title };
}

/** The header's clock: it counts while Marvin works, and shows until the answer starts or once the turn ended. */
export function turnClock(
    phase: TurnPhase,
    turn: TurnModel,
): { running: boolean; visible: boolean } {
    const live = phase === "live";
    const ended = phase === "cancelled" || phase === "error";
    return {
        running: live && !turn.waiting,
        visible: (live && !turn.hasText && !turn.waiting) || ended,
    };
}

/**
 * The window over a long list. Past {@link WINDOW_FROM_ROWS} steps only the newest
 * {@link WINDOW_ROWS} show until asked: `hidden` steps sit under "Show N earlier steps" and the
 * shown rows start at `cut`. Windowing counts steps only; a narration hides with the steps it sits
 * among, but the one right before the first shown step stays to introduce it.
 */
export function stepWindow(
    rows: TurnStep[],
    showAll: boolean,
): { windowed: boolean; hidden: number; cut: number } {
    const stepRows = rows.filter(isCountedStep);
    const windowed = stepRows.length > WINDOW_FROM_ROWS;
    const hidden = windowed && !showAll ? stepRows.length - WINDOW_ROWS : 0;
    let cut = 0;
    if (hidden > 0) {
        cut = rows.indexOf(stepRows[hidden]);
        while (cut > 0 && rows[cut - 1].kind === "narration") cut -= 1;
    }
    return { windowed, hidden, cut };
}

/**
 * A turn's answer and cards cut after each question, one run per segment of
 * `buildTurnSegments`: a segment's steps sit between the question that ends the one before and
 * the question that ends it. Segments with nothing to show get an empty run.
 */
export function contentBySegment<T extends { kind: string }>(items: T[], segments: number): T[][] {
    const cut: T[][] = [[]];
    for (const item of items) {
        cut[cut.length - 1].push(item);
        if (item.kind === "question") cut.push([]);
    }
    while (cut.length < segments) cut.push([]);
    return cut;
}

/** One row of a step list that groups repeats: a step, and how many identical steps in a row it stands for. */
export interface GroupedRow {
    step: TurnStep;
    /** 1 for a step that stands alone. */
    times: number;
    /** What the row found, once for the whole run: kept only when every step in it found the same. */
    result: string | null;
}

/** Two rows read the same and can share one: finished work in the same words, by Marvin himself. */
function repeats(a: TurnStep, b: TurnStep): boolean {
    return (
        isCountedStep(a) &&
        isCountedStep(b) &&
        a.id !== WAITING_ROW_ID &&
        b.id !== WAITING_ROW_ID &&
        a.status !== "running" &&
        b.status !== "running" &&
        !a.helper &&
        !b.helper &&
        a.op === b.op &&
        a.status === b.status &&
        rowText(a) === rowText(b)
    );
}

/**
 * Consecutive steps that read the same ("Read part of a sheet", twice) as one row: "Read part of a
 * sheet · 2 times" (hp, 2026-10-09: repeated steps grouped). A presentation option the native
 * engine applies; the web lists each step. The row keeps its first step's id, so it stays the same
 * row as more repeats join it, and the running step always has a row of its own.
 */
export function groupRepeatedSteps(rows: TurnStep[]): GroupedRow[] {
    const grouped: GroupedRow[] = [];
    let previous: TurnStep | null = null;
    for (const step of rows) {
        const last = grouped[grouped.length - 1];
        if (last && previous && repeats(previous, step)) {
            last.times += 1;
            if (last.result !== rowResult(step)) last.result = null;
        } else {
            grouped.push({ step, times: 1, result: rowResult(step) });
        }
        previous = step;
    }
    return grouped;
}

/** "2 times": how many times a grouped row's step ran. */
export function timesText(times: number): string {
    return `${times} times`;
}

/** Where the time a turn ended goes in its end notice ("4:34 PM"), for the client that saw it end. */
export const TIME_TOKEN = "{time}";

/** The notice under a turn that did not finish (designs B8 and B9). */
export interface TurnEndWords {
    /** The user stopped it, or its stream failed. */
    kind: "stopped" | "dropped";
    /** Its words with the time it ended at {@link TIME_TOKEN}. */
    timed: string;
    /** Its words when the time is not known (a turn reloaded after it ended). */
    untimed: string;
    /** The button that sends the question again; a turn stopped after its answer began has none. */
    action?: "Ask again" | "Retry";
}

/** What a stopped or dropped turn says under it, from what had arrived; none for any other turn. */
export function turnEndWords(
    phase: TurnPhase,
    hasText: boolean,
    hasSteps: boolean,
): TurnEndWords | null {
    if (phase === "cancelled") {
        return hasText
            ? { kind: "stopped", timed: "Stopped by you", untimed: "Stopped by you" }
            : {
                  kind: "stopped",
                  timed: `You stopped Marvin at ${TIME_TOKEN}, before an answer.`,
                  untimed: "You stopped Marvin before an answer.",
                  action: "Ask again",
              };
    }
    if (phase !== "error") return null;
    const rest = hasText
        ? ", before the answer finished."
        : hasSteps
          ? ". The steps above finished; the answer didn't arrive."
          : ", before the answer arrived.";
    return {
        kind: "dropped",
        timed: `The connection dropped at ${TIME_TOKEN}${rest}`,
        untimed: `The connection dropped${rest}`,
        action: "Retry",
    };
}

/** A notice's words with the time it ended put in, or its untimed words when the time is not known. */
export function endNoticeText(words: TurnEndWords, time: string | null): string {
    return time === null ? words.untimed : words.timed.replace(TIME_TOKEN, time);
}
